import { put, call, fork } from 'redux-saga/effects';
import * as actionsNO05 from '../actions/indexNO05';
import * as APICalls from '../../felles/APICalls_NO05';
import NO05 from '../../models/NO05';
import { computeStats } from '../../pages/NO05/no05Stats';

function getErrorMessage(error: any, fallback: string): string {
    const backendMessage = error?.response?.data?.error;
    return typeof backendMessage === 'string' && backendMessage.trim() ? backendMessage : fallback;
}

function mapToNO05(item: any): NO05 {
    const lines: any[] = item.products ?? [];
    const antBestilt = lines.reduce((s: number, l: any) => s + (l.quantityOrdered ?? 0), 0);
    const antLevert = lines.reduce((s: number, l: any) => s + (l.quantityDelivered ?? 0), 0);

    // Retur orders (order number starting with '9') are registered with a positive amount in the
    // backend, but represent a credit — force the sign negative here so the row and the status
    // card totals (computeStats in no05Stats.ts sums `belop`, incl. the "Kredit" card and "Alle ordre") read
    // as a minus instead of adding the return on top of the total.
    const isRetur = (item.orderNumber ?? '').toString().startsWith('9');
    const rawTotalPrice = item.totalPrice ?? 0;
    const belop = isRetur ? -Math.abs(rawTotalPrice) : rawTotalPrice;

    return new NO05(
        item.orderNumber ?? '',
        item.customerNumber ?? '',
        item.customerName ?? '',
        item.requisitionNumber ?? '',
        item.orderDate ?? '',
        item.orderTime ?? '',
        item.receiver ?? '',
        item.status ?? 'UFULLSTENDIG INNLAGT',
        antBestilt, antLevert,
        belop,
        item.grossProfit ?? 0,
        // Already negated by the backend for credit orders, unlike `belop` above — don't flip
        // the sign again here.
        item.grossProfitRevenue ?? 0,
        item.grossProfitCost ?? 0,
        item.invoiceCreditNumber ?? '',
        item.shippingNumber ?? '',
        String(item.orderType ?? ''),
        item.orderPaymentMethod ?? '',
        item.shippingMethod ?? '',
        item.speditor ?? '',
        String(item.directOrderFlag ?? ''),
        String(item.skaff ?? ''),
        String(item.bk ?? ''),
        String(item.recordType ?? ''),
    );
}

// Initial page load, split into two independent, concurrent fetches instead of one combined
// call — the table no longer has to wait on the slow full/unpaged fetch that the status cards
// need for accurate counts:
//  - quickInitialTableLoad: one fast, small page → table renders almost immediately.
//  - fullInitialStatsLoad: the full open-order set (unchanged from before) → status cards
//    (hidden until this arrives, same as today), and once it lands it also replaces the
//    table's quick page with the complete set so status-card filtering has everything to
//    filter against. Forked so a rapid second search (takeLatest) cancels both cleanly.
function* quickInitialTableLoad(companyId: string) {
  try {
    const response: any = yield call(APICalls.searchOpenOrders, { companyId, page: 0, pageSize: 30 });
    if (response?.data) {
      const raw: any[] = response.data.orders ?? response.data.content ?? [];
      // hasMore here just controls scroll-triggered paging until the full fetch below lands
      // and replaces this quick page outright (see fullInitialStatsLoad).
      yield put(actionsNO05.NO05SearchSuccess(raw.map(mapToNO05), raw.length >= 30));
    }
  } catch (error: any) {
    yield put(actionsNO05.NO05SearchFailure(getErrorMessage(error, 'Kunne ikke hente åpne ordre. Prøv igjen.')));
  }
}

function* fullInitialStatsLoad(companyId: string) {
  try {
    yield put(actionsNO05.NO05StatsRequest(companyId));
    const response: any = yield call(APICalls.loadOpenOrdersStats, companyId);
    if (response?.data) {
      const raw: any[] = response.data.orders ?? response.data.content ?? [];
      const orders: NO05[] = raw.map(mapToNO05);
      yield put(actionsNO05.NO05StatsSuccess(computeStats(orders), orders));
      yield put(actionsNO05.NO05SearchSuccess(orders, false));
    }
  } catch (error: any) {
    // Only the stats/cards side fails here — the table already has the quick page from
    // quickInitialTableLoad (a genuinely separate, unrelated request), and NO05SearchFailure
    // would wipe that out (the reducer clears `orders` on that action). Leave it standing.
    yield put(actionsNO05.NO05StatsFailure(getErrorMessage(error, 'Kunne ikke hente statuskort. Prøv igjen.')));
  }
}

export function* searchNO05OrdersSaga(action: any) {
  try {
    const isInitialLoad = action.payload.skipPaging === true;

    if (isInitialLoad) {
      yield fork(quickInitialTableLoad, action.payload.companyId);
      yield fork(fullInitialStatsLoad, action.payload.companyId);
    } else {
      // Filtered search — stats reflect the search results
      const response: any = yield call(APICalls.searchOpenOrders, { ...action.payload, page: 0, pageSize: 100 });
      if (response?.data) {
        const raw: any[] = response.data.orders ?? response.data.content ?? [];
        const orders: NO05[] = raw.map(mapToNO05);
        yield put(actionsNO05.NO05StatsSuccess(computeStats(orders), orders));
        yield put(actionsNO05.NO05SearchSuccess(orders, raw.length >= 100));
      }
    }
  } catch (error: any) {
    yield put(actionsNO05.NO05SearchFailure(getErrorMessage(error, 'Kunne ikke søke etter ordre. Prøv igjen.')));
  }
}

export function* loadMoreNO05OrdersSaga(action: any) {
  try {
    // Strip skipPaging — load-more always uses regular paging
    const { skipPaging: _, ...pagedParams } = action.payload;
    const response = yield call(APICalls.searchOpenOrders, pagedParams);
    if (response?.data) {
      const raw: any[] = response.data.orders ?? response.data.content ?? [];
      const newOrders: NO05[] = raw.map(mapToNO05);
      const hasMore = raw.length >= (pagedParams.pageSize ?? 30);

      yield put(actionsNO05.NO05LoadMoreSuccess(newOrders, hasMore));
    }
  } catch (error: any) {
    yield put(actionsNO05.NO05LoadMoreFailure(getErrorMessage(error, 'Kunne ikke laste flere ordre. Prøv igjen.')));
  }
}

export function* fetchNO05OrderDetailSaga(action: any) {
  try {
    const { companyId, orderNumber } = action.payload;
    const response = yield call(APICalls.fetchOrderDetail, companyId, orderNumber);
    yield put(actionsNO05.NO05OrderDetailSuccess(response.data));
  } catch (error: any) {
    yield put(actionsNO05.NO05OrderDetailFailure(getErrorMessage(error, 'Kunne ikke hente ordredetaljer. Prøv igjen.')));
  }
}
